import { TextMessage } from 'components/Dialogue';
import { BaseMsgProps } from './types';
import { getMetaFromSender, injectQuote } from '../../utils';
import { useChatContext } from '../../contexts/ChatContext';
import QuoteMessage from '../QuoteMessage';
import sysLangToTranslationLang from 'utils/sysLangToTranslationLang';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import { MessageExtendComplex } from '../MessageExtend/MessageExtendComplex';
import { memo, useCallback, useContext } from 'react';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { DialogueModeContext } from '../DialogueModeContext.tsx';
import { css } from '@emotion/css';
import { stripHtmlTag } from 'utils/stripHtmlTag.ts';

export const TextMsg = memo(
  (
    props: BaseMsgProps & {
      isFold?: boolean;
    },
  ) => {
    const { raw, msg, conversation, isFold } = props;
    const { singleTransToSysLang, clickQuote } = useChatContext();
    const { getTranslation } = useGetTranslation();
    const isFaq = useContext(DialogueModeContext) === 'faq';

    const { botReplyType, textContent } = raw;
    const { sender } = msg;
    const { topBarInfo } = conversation;
    const { layout, color } = getMetaFromSender(sender);

    injectQuote(raw);

    const content = (
      <TextMessage
        conversation={conversation}
        className={isFold ? `${layout} ${foldClassName}` : layout}
        layout={layout}
        color={isFaq ? '#fff' : botReplyType && botReplyType !== 'BRT_UNKNOWN' ? '#F6FFF9' : color}
        content={stripHtmlTag(textContent?.text || '')}
        translation={getTranslation(textContent?.translationContent)}
        msg={msg.raw}
        onTranslate={singleTransToSysLang}
        quote={
          isFold ? null : (
            <QuoteMessage
              message={raw}
              storeName={topBarInfo?.storeName}
              onQuoteClick={clickQuote}
            />
          )
        }
      />
    );

    if (isFold) {
      return content;
    }

    return (
      <MessageExtendComplex raw={raw} msg={msg} conversation={conversation} content={content} />
    );
  },
);

export function useGetTranslation() {
  const sysLang = useSystemLanguageValue();
  const { whiteListConfig: { IGNORE_SHOPEE_TRANSLATION } = {} } = useOrgConfigStateValue();
  return {
    sysLang,
    getTranslation: useCallback(
      (translationContent?: Conversation.MsgText['translationContent']) => {
        const result = translationContent?.[sysLangToTranslationLang(sysLang)];
        if (!IGNORE_SHOPEE_TRANSLATION) {
          return result;
        }
        return result?.source === 'shopee' ? undefined : result;
      },
      [sysLang, IGNORE_SHOPEE_TRANSLATION],
    ),
  };
}

const foldClassName = css`
  > section {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;
